import type {EmailState} from 'src/reducers/email';
import type {
  DynamicLabelExtra as DynamicLabel,
  DynamicLabels,
} from 'src/types/dynamic-labels';
import type {MenuGroupTitleOption} from '@spaced-out/ui-design-system/lib';
import type {EntityType} from 'src/types/ats-entities';
import groupBy from 'lodash/groupBy';
import type {
  Configs,
  EmailApi,
  EmailAttachment,
  Validations,
} from 'src/types/email';
import type {
  EntityGroupApi,
  EntityMappingsByEntityType,
  IncomingRelatedEntity,
  StandardEntity,
} from 'src/types/target-entity-types';
import {CHATBOT_ADDON_ID} from 'src/components/automation/common/chatbot-addon';
import {SURVEY_ADDON_ID} from 'src/components/automation/common/survey-addon';
import {SCHEDULER_ADDON_ID} from 'src/components/scheduler/scheduler-email-addon/scheduler-addon';
import {JOB_MATCH_ADDON_ID} from 'src/constants/job-match-addon';
import {REFERRAL_ADDON_ID} from 'src/utils/referral-node';
import type {ReferralMetadata} from 'src/utils/referral-node';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import omit from 'lodash/omit';
import {
  normalizeNodeSchedule,
  parseNodeSchedule,
} from 'src/utils/node-schedule';
import {
  parseNoteWriteback,
  normalizeNoteWriteback,
} from 'src/utils/note-writeback';


export const SIMPLE_TEMPLATE_START_TEXT = 'Start typing here...';
export const SUPPORTED_CHATBOT_USECASES = [
  'data_enrichment',
  'prescreening',
] as const;
export const VALID_ATTACHMENTS = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/octet-stream': [
    '.pdf',
    '.ppt',
    '.pptx',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.odt',
    '.rtf',
  ],
  'text/plain': ['.txt'],
  'application/vnd.ms-excel': ['.csv'], //Windows recognizes .csv files as a different type (https://github.com/react-dropzone/react-dropzone/issues/276)
  'text/csv': ['.csv'],
} as const;

export const MAX_EMAIL_ATTACHMENTS_SIZE = 10 * 1024 * 1024;

export const API_STATE_KEY_MAP = {
  bcc: 'bcc',
  cc: 'cc',
  from: 'from_email',
  to: 'to_email',
  replyTo: 'reply_to',
} as const;

export const HF_EXCEPTION_LABELS_EMAIL = {
  used_in_condition: 'Previous Node Variables used in email conditions',
  used_in_body: 'Previous Node Variables used in email body',
  used_in_settings: 'Previous Node Variables used in email settings',
} as const;

export const normalizeEmail = (
  emailState: EmailState,
  isPreview: boolean | null = false,
): EmailApi => {
  const content = {
    type: 'BEEFREE',
    content_json: normalizeJobMatchAddon(emailState.content_json),
    content_html: emailState.content_html,
  } as const;
  const from = {
    name: emailState.from_name,
    email: emailState.from_email,
  } as const;
  const to = {
    name: null,
    email: emailState.to_email,
  } as const;

  const cc = emailState.cc.map((email) => ({name: null, email}));
  const bcc = emailState.bcc.map((email) => ({name: null, email}));
  const reply_to = emailState.replyTo
    ? {
        name: null,
        email: emailState.replyTo.email,
      }
    : null;
  const content_subscription = {
    category_id: emailState.content_subscription_category_id,
    reason: emailState.content_subscription_reason,
    skip_subscription_link: emailState.skip_subscription_link,
  } as const;

  const attachments = (emailState.attachments ?? []).map(
    ({file_name, id, size, value, type}) => ({
      file_name,
      id,
      size,
      value,
      type,
    }),
  );
  const nodeSchedule = normalizeNodeSchedule(emailState);
  const noteWriteback = normalizeNoteWriteback(emailState);
  return {
    task: {
      params: {
        task_definition: {
          content,
          subject: emailState.subject,
          from,
          to,
          cc,
          bcc,
          reply_to,
          content_subscription,
          attachments,
        },
      },
    },
    //attach scheduling fields to be sent to backend
    ...nodeSchedule,
    ...noteWriteback,
    // Omit when undefined (API didn't return it and user didn't set it); send number or null when known.
    ...(emailState.email_template_id !== undefined
      ? {email_template_id: emailState.email_template_id}
      : {}),
  };
};

export const parseEmail = (emailApiResponse: EmailApi): EmailState => {
  const emailSendTask = emailApiResponse.task;
  const taskDefinition = emailSendTask.params.task_definition;
  if (!taskDefinition) {
    throw new Error('Email task definition not found');
  }
  const nodeSchedule = parseNodeSchedule(emailApiResponse);
  const noteWriteback = parseNoteWriteback(emailApiResponse);
  return {
    // undefined when API omitted the key (don't overwrite on save); number | null when present.
    email_template_id:
      'email_template_id' in emailApiResponse
        ? emailApiResponse.email_template_id
        : undefined,
    subject: taskDefinition.subject,
    content_json: parseJobMatchAddon(taskDefinition.content.content_json),
    content_html: taskDefinition.content.content_html,
    from_name: taskDefinition.from.name,
    from_email: taskDefinition.from.email,
    to_email: taskDefinition.to.email,
    cc: taskDefinition.cc.map((item) => item.email),
    bcc: taskDefinition.bcc.map((item) => item.email),
    replyTo: taskDefinition.reply_to?.email
      ? {
          name: taskDefinition.reply_to.name ?? null,
          email: taskDefinition.reply_to.email,
        }
      : null,
    content_subscription_category_id:
      taskDefinition.content_subscription.category_id,
    content_subscription_reason: taskDefinition.content_subscription.reason,
    skip_subscription_link:
      taskDefinition.content_subscription.skip_subscription_link,
    attachments:
      taskDefinition.attachments?.map((attachment: EmailAttachment) => ({
        ...attachment,
        reject: false,
        rejectReason: '',
        progress: false,
      })) ?? [],
    chatbotsAttached: taskDefinition.chatbots_attached ?? [],
    surveysAttached: taskDefinition.surveys_attached ?? [],
    meetingsAttached: taskDefinition.meetings_attached ?? [],
    ...nodeSchedule,
    ...noteWriteback,
  };
};

export const getVariableOptions = (
  dynamicLabels: DynamicLabels,
): Array<MenuGroupTitleOption> => {
  const options: Array<MenuGroupTitleOption> = [];
  const variableMap = groupBy(dynamicLabels, (dl) => dl.mapping.display_name);
  for (const [key, value] of Object.entries(variableMap)) {
    const optionsObj = {
      groupTitle: key,
      options: value.map(({resolvedId, label}) => ({key: resolvedId, label})),
    } as const;
    options.push(optionsObj);
  }
  return options;
};
export const getNodeConfigs = (
  values: Array<string>,
  dynamicLabels: DynamicLabels,
  entityType: string,
  configs: Configs,
): Configs => {
  const rteOptionMap = dynamicLabels.reduce<Record<string, any>>(
    (acc, option) => {
      acc[option.rteValue] = option;
      return acc;
    },
    {},
  );
  const localConfig = cloneDeep(configs);
  values.forEach((rteValue) => {
    if (rteOptionMap[rteValue]) {
      const key = rteOptionMap[rteValue].value;
      const relationArray = key ? key.split('/') : [];
      if (relationArray.length > 0) {
        relationArray.unshift(entityType);
        localConfig.dynamic_variables[key] = relationArray;
      }
    }
  });
  return !isEmpty(localConfig) ? localConfig : {dynamic_variables: {}};
};

export const extractRTELabelsFromString = (
  inputString?: string | null,
  // @ts-expect-error - TS7030 - Not all code paths return a value.
): Array<string> | null | undefined => {
  try {
    const regex = /{{(.*?)}}/g;
    const matches = inputString && inputString.match(regex);
    return matches ? matches : [];
  } catch (e: any) {
    console.error('Invalid JSON Input', e.message);
  }
};

const getAttributeNameFromValue = (value: string): string =>
  // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
  value ? value.split('/').pop() : value;

export const generateDynamicLabelConfig = (
  labels: Array<string>,
  dynamicLabels: DynamicLabels,
  baseEntity: string,
): Record<any, any> => {
  const config: Record<string, any> = {};
  const valueOptionMap = dynamicLabels.reduce<Record<string, any>>(
    (acc, option) => {
      acc[option.value] = option;
      return acc;
    },
    {},
  );
  labels.forEach((label) => {
    const param: Record<string, any> = {};
    const relationArray = label ? label.split('/') : [];
    const fieldValue = label;
    if (relationArray.length > 0) {
      param.from_relations = relationArray.slice(0, -1);
      param.attribute_name = getAttributeNameFromValue(fieldValue);
      param.entity_type = valueOptionMap[label].entity_type;
      param.base_entity_type = baseEntity;
    }
    config[fieldValue] = param;
  });
  return config;
};

export const getEmsFetchDynamicLabels = (
  emailState: EmailState,
  dynamicLabels: DynamicLabels,
  isPreview: boolean | null = false,
): Array<string> => {
  const emsDynamicLabels: Array<any> = [];
  function populateEmsDynLabelsArray(rteValue: string | null | undefined) {
    if (rteValue && rteOptionMap[rteValue]) {
      emsDynamicLabels.push(rteOptionMap[rteValue].value);
    }
  }
  const rteOptionMap = dynamicLabels.reduce<Record<string, any>>(
    (acc, option) => {
      acc[option.rteValue] = option;
      return acc;
    },
    {},
  );
  extractRTELabelsFromString(emailState.content_json)?.forEach(
    populateEmsDynLabelsArray,
  );
  if (!isPreview) {
    emailState.cc.forEach(populateEmsDynLabelsArray);
    emailState.bcc.forEach(populateEmsDynLabelsArray);
    if (emailState.replyTo?.email) {
      populateEmsDynLabelsArray(emailState.replyTo.email);
    }
    populateEmsDynLabelsArray(emailState.from_email);
    populateEmsDynLabelsArray(emailState.to_email);
    extractRTELabelsFromString(emailState.from_name)?.forEach(
      populateEmsDynLabelsArray,
    );
    extractRTELabelsFromString(emailState.subject)?.forEach(
      populateEmsDynLabelsArray,
    );
  }
  return uniq(emsDynamicLabels);
};
export const getUsedEmsDynamicLabels = (
  emailState: EmailState,
  dynamicLabels: DynamicLabels,
  isPreview: boolean | null = false,
): Map<string, DynamicLabel> => {
  const usedEmsDynamicLabels = new Map<string, DynamicLabel>();
  const rteOptionMap = dynamicLabels.reduce<Record<string, any>>(
    (acc, option) => {
      acc[option.rteValue] = option;
      return acc;
    },
    {},
  );
  function populateEmsDynLabels(rteValue: string | null | undefined) {
    if (rteValue && rteOptionMap[rteValue]) {
      usedEmsDynamicLabels.set(rteValue, rteOptionMap[rteValue]);
    }
  }

  extractRTELabelsFromString(emailState.content_json)?.forEach(
    populateEmsDynLabels,
  );
  if (!isPreview) {
    emailState.cc.forEach(populateEmsDynLabels);
    emailState.bcc.forEach(populateEmsDynLabels);
    if (emailState.replyTo?.email) {
      populateEmsDynLabels(emailState.replyTo.email);
    }
    populateEmsDynLabels(emailState.from_email);
    populateEmsDynLabels(emailState.to_email);
    extractRTELabelsFromString(emailState.from_name)?.forEach(
      populateEmsDynLabels,
    );
    extractRTELabelsFromString(emailState.subject)?.forEach(
      populateEmsDynLabels,
    );
  }
  return usedEmsDynamicLabels;
};

export const generateChatbotConfig = (
  emailState: EmailState,
): Record<any, any> => {
  let rows = [];
  if (emailState.content_json) {
    try {
      rows = JSON.parse(emailState.content_json)?.page?.rows ?? [];
    } catch (ex: any) {}
  }
  // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments.
  const params = (rows ?? [])
    // @ts-expect-error - TS7006 - Parameter 'row' implicitly has an 'any' type.
    .filter((row) => rowContainsChatbot(row))
    // @ts-expect-error - TS7006 - Parameter 'row' implicitly has an 'any' type.
    .map((row) => row.metadata)
    // @ts-expect-error - TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7006 - Parameter 'metadata' implicitly has an 'any' type.
    .reduce<Record<string, any>>((acc, metadata) => {
      const chatbot = omit(metadata, 'chatbotType');
      return {...acc, ...chatbot};
    }, {});

  return params;
};

export const rowContainsChatbot = (row: {
  rowInternal: {
    uid: string;
  };
  columns: Array<{
    modules: Array<Record<any, any>>;
  }>;
}): boolean => {
  if (!row.rowInternal) {
    return false;
  }
  if (row.rowInternal.uid !== 'sense-chatbot-row') {
    return false;
  }
  if (row.columns.length !== 1) {
    return false;
  }
  if (row.columns[0].modules.length === 0) {
    return false;
  }
  return true;
};
export const getErrors = (validationsObject: Validations): Array<string> => {
  const errors: Array<string> = [];
  const {
    senderNameEmpty,
    senderEmailEmpty,
    emailSubjectEmpty,
    emailSubjectTooLong,
    sendToEmailEmpty,
    contentSubEmpty,
    sameFromToEmail,
    invalidFromEmail,
    invalidToEmail,
    nwbEntityNotSelected,
    nwbNoteActionNotSelected,
    contentSubCategoryNotSelected,
    nwbReceiverFieldIsInvalid,
  } = validationsObject;
  if (
    senderNameEmpty ||
    senderEmailEmpty ||
    emailSubjectEmpty ||
    sendToEmailEmpty ||
    contentSubEmpty ||
    contentSubCategoryNotSelected
  ) {
    errors.push('Please fill the required fields.');
  }
  if (sameFromToEmail) {
    errors.push('Sender Email cannot be same as Receiver Email.');
  }
  if (invalidFromEmail) {
    errors.push('Sender Email is Invalid.');
  }
  if (invalidToEmail) {
    errors.push('Receiver Email is Invalid.');
  }

  if (emailSubjectTooLong) {
    errors.push('Subject text is too long.');
  }
  if (nwbEntityNotSelected) {
    errors.push('Entity field cannot be empty.');
  }
  if (nwbNoteActionNotSelected) {
    errors.push('Action field cannot be empty.');
  }
  if (nwbReceiverFieldIsInvalid) {
    errors.push(
      'Note Write Back can only be enabled if Receiver Email is set to an entity or global variable. Please update the Receiver Email field accordingly.',
    );
  }
  return errors;
};

const flattenEntityGroup = (
  entityGroupData: Record<any, any>,
  parentKey: string = '',
): Record<any, any> => {
  const result: Record<string, any> = {};
  for (const key in entityGroupData) {
    const newKey = parentKey ? `${parentKey}/${key}` : key;
    result[newKey] = entityGroupData[key];
  }
  return result;
};

export const getAttributeDataFromEntityGroup = (
  entityGroup: EntityGroupApi,
  parentKey: string = '',
): Record<any, any> => {
  let result: Record<string, any> = {};
  for (const key in entityGroup.relatedEntities) {
    const combinedKey = parentKey ? `${parentKey}/${key}` : key;
    result = {
      ...result,
      ...getAttributeDataFromEntityGroup(
        entityGroup.relatedEntities[key],
        combinedKey,
      ),
    };
  }
  result = {
    ...result,
    ...flattenEntityGroup(entityGroup.entity.data, parentKey),
  };
  return result;
};

export const getIncomingRelatedEntityGroupIds = (
  incomingRelatedEntities: Array<IncomingRelatedEntity>,
  standardWorkflowEntity: StandardEntity,
): Array<{
  key: string;
  label: string;
  // carry extra info needed by sender-name API
  meta?: {
    externalSourceId?: string;
    entityType?: string;
  };
}> => {
  const targetEntityGroup = incomingRelatedEntities.find(
    (incomingEntity) =>
      incomingEntity.standard_entity_type === standardWorkflowEntity,
  );
  if (targetEntityGroup) {
    return targetEntityGroup.incoming_related_entity_groups.map(
      ({entity, entityType}) => {
        return {
          key: entity.id,
          //Todo(Vish): We need to append a display name before externalSourceId here. Discuss with Backend
          label: `ID: ${entity.externalSourceId}`,
          meta: {
            externalSourceId: entity.externalSourceId,
            entityType,
          },
        };
      },
    );
  }
  return [];
};

/**
 * sorts variables so that event variables that do not exist for the person entity or related entity are at the top,
 * followed by event variables that exist but has no value,
 * and finally event variables with values
 */
export const getSortedEventVariables = ({
  flattenedEntityVariables,
  uniqueEntityVariablesInEvent,
  dynamicLabelsByValue,
}: {
  flattenedEntityVariables: {
    [key: string]: unknown;
  };
  uniqueEntityVariablesInEvent: Array<string>;
  dynamicLabelsByValue: {
    [key: string]: DynamicLabel;
  };
}): Array<DynamicLabel> => {
  const variablesWithValues: Array<any> = [];
  const nonexistantVariables: Array<any> = [];
  const variablesWithEmptyFields: Array<any> = [];

  for (const variable of uniqueEntityVariablesInEvent) {
    const entityValue = variable && flattenedEntityVariables[variable];
    if (!dynamicLabelsByValue[variable]) {
      continue;
      //do not include variables not in the dynamicLabels map
    } else if (entityValue === null || entityValue === '') {
      variablesWithEmptyFields.push({
        ...dynamicLabelsByValue[variable],
        entityValue: '(field empty)',
      });
    } else if (entityValue === undefined) {
      nonexistantVariables.push({
        ...dynamicLabelsByValue[variable],
        entityValue: '(does not exist)',
      });
    } else {
      variablesWithValues.push({
        ...dynamicLabelsByValue[variable],
        entityValue,
      });
    }
  }

  //concating three arrays to ensure the correct order based on variable value state
  return [
    ...nonexistantVariables,
    ...variablesWithEmptyFields,
    ...variablesWithValues,
  ];
};

export const getTotalChatbotsFromBeefreeJSON = (content: string): number => {
  if (content === '{}') {
    return 0;
  }
  const contentJSON = JSON.parse(content);
  const emailRows = contentJSON?.page?.rows;
  // @ts-expect-error - TS7006 - Parameter 'chatbotCount' implicitly has an 'any' type. | TS7006 - Parameter 'row' implicitly has an 'any' type.
  const chatbotCount = emailRows.reduce((chatbotCount, row) => {
    if (row.rowInternal && row.rowInternal.uid === CHATBOT_ADDON_ID) {
      return row?.metadata && !isEmpty(row.metadata)
        ? chatbotCount + 1
        : chatbotCount;
    }
    return chatbotCount;
  }, 0);
  return chatbotCount;
};

export const getTotalSurveysFromBeefreeJSON = (content: string): number => {
  if (content === '{}') {
    return 0;
  }
  const contentJSON = JSON.parse(content);
  const emailRows = contentJSON?.page?.rows;
  // @ts-expect-error - TS7006 - Parameter 'surveyCount' implicitly has an 'any' type. | TS7006 - Parameter 'row' implicitly has an 'any' type.
  const surveyCount = emailRows.reduce((surveyCount, row) => {
    if (row.rowInternal && row.rowInternal.uid === SURVEY_ADDON_ID) {
      return row?.metadata && !isEmpty(row.metadata)
        ? surveyCount + 1
        : surveyCount;
    }
    return surveyCount;
  }, 0);
  return surveyCount;
};

export const getTotalSchedulesFromBeefreeJSON = (content: string): number => {
  if (content === '{}') {
    return 0;
  }
  const contentJSON = JSON.parse(content);
  const emailRows = contentJSON?.page?.rows;
  // @ts-expect-error - TS7006 - Parameter 'surveyCount' implicitly has an 'any' type. | TS7006 - Parameter 'row' implicitly has an 'any' type.
  const scheduleCount = emailRows.reduce((scheduleCount, row) => {
    if (row.rowInternal && row.rowInternal.uid === SCHEDULER_ADDON_ID) {
      return row?.metadata && !isEmpty(row.metadata)
        ? scheduleCount + 1
        : scheduleCount;
    }
    return scheduleCount;
  }, 0);
  return scheduleCount;
};

export const getTotalJobMatchesFromBeefreeJSON = (content: string): number => {
  if (content === '{}') {
    return 0;
  }
  const contentJSON = JSON.parse(content);
  const emailRows = contentJSON?.page?.rows;
  if (!Array.isArray(emailRows)) {
    return 0;
  }
  const jobMatchCount = emailRows.reduce(
    (
      jobMatchCount: number,
      row: {rowInternal: {uid: string}; metadata: Record<string, any>},
    ) => {
      if (row.rowInternal && row.rowInternal.uid === JOB_MATCH_ADDON_ID) {
        return row?.metadata && !isEmpty(row.metadata)
          ? jobMatchCount + 1
          : jobMatchCount;
      }
      return jobMatchCount;
    },
    0,
  );
  return jobMatchCount;
};

export const getReferralMetadataFromBeefreeJSON = (
  content: string,
): ReferralMetadata | null => {
  if (content === '{}') {
    return null;
  }
  const contentJSON = JSON.parse(content);
  const emailRows = contentJSON?.page?.rows;
  if (!Array.isArray(emailRows)) {
    return null;
  }
  for (const row of emailRows) {
    if (
      row.rowInternal &&
      row.rowInternal.uid === REFERRAL_ADDON_ID &&
      row.value?.metadata
    ) {
      return row.value.metadata as ReferralMetadata;
    }
  }
  return null;
};

export const validateEmailBeefreeJson = (
  content: string,
): string | null | undefined => {
  if (content === '{}') {
    return 'No content found in the email';
  }
  const contentJSON = JSON.parse(content);
  const emailRows = contentJSON?.page?.rows;

  //check if no placeholder content is present for chatbot,
  // if yes, don't let the user save the content
  // @ts-expect-error - TS7006 - Parameter 'row' implicitly has an 'any' type.
  const containsChatbotPlaceholder = emailRows.some((row) => {
    if (row.rowInternal && row.rowInternal.uid === CHATBOT_ADDON_ID) {
      return row.empty;
    }
    return false;
  });

  // @ts-expect-error - TS7006 - Parameter 'row' implicitly has an 'any' type.
  const containsSurveyPlaceholder = emailRows.some((row) => {
    if (row.rowInternal && row.rowInternal.uid === SURVEY_ADDON_ID) {
      return row.empty;
    }
    return false;
  });

  const containsJobMatchPlaceholder = emailRows.some(
    (row: {rowInternal: {uid: string}; empty: boolean}) => {
      if (row.rowInternal && row.rowInternal.uid === JOB_MATCH_ADDON_ID) {
        return row.empty;
      }
      return false;
    },
  );

  if (containsSurveyPlaceholder && containsChatbotPlaceholder) {
    return 'Configure the survey and chatbot add-ons to save the design';
  }

  if (containsSurveyPlaceholder) {
    return 'Configure the survey add-on to save the design';
  }

  if (containsChatbotPlaceholder) {
    return 'Configure the chatbot add-on to save the design';
  }

  if (containsJobMatchPlaceholder) {
    return 'Configure the job match add-on to save the design';
  }
  return null;
};

export const cleanupEmailBeefreeJson = (contentJSON: string): string => {
  if (contentJSON === '{}') {
    return contentJSON;
  }
  const content = JSON.parse(contentJSON);
  const emailRows = content?.page?.rows;
  /** NOTE(diwakersurya) below code removes the empty chatbot-addon-rows.
   * this solves the issue when user remove chatbot addon row content but does not
   * remove the row itself. this can be better solved if beefree provides a way
   * to prevent the deletion of content through the ui. We should only allow the
   * deletion of addon row itself along with its content and not the content
   * separately
   */
  if (emailRows) {
    // @ts-expect-error - TS7006 - Parameter 'row' implicitly has an 'any' type.
    const cleanedEmailRows = emailRows.filter((row) => {
      if (
        row.rowInternal &&
        (row.rowInternal.uid === CHATBOT_ADDON_ID ||
          row.rowInternal.uid === SURVEY_ADDON_ID ||
          row.rowInternal.uid === JOB_MATCH_ADDON_ID)
      ) {
        //currently chatbot row has only one column
        //get the first column
        const firstColumn = row.columns?.[0];
        if (!firstColumn) {
          return false;
        }
        // if there are no modules, it means, there is no content in the column
        // hence this row should be removed
        return firstColumn.modules.length > 0;
      }
      return true;
    });
    content.page.rows = cleanedEmailRows;
  }
  return JSON.stringify(content);
};

export const isBeefreeContentEmpty = (
  beefreeJson: Record<any, any> | null | undefined,
): boolean => {
  const rows = beefreeJson?.page?.rows;
  if (!Array.isArray(rows) || rows.length === 0) {
    return true;
  }

  const hasContentRow = rows.some(
    (row: {columns?: Array<{modules?: Array<any>}>}) => {
      if (!Array.isArray(row.columns) || row.columns.length === 0) {
        return false;
      }
      return row.columns.some(
        (col) => Array.isArray(col.modules) && col.modules.length > 0,
      );
    },
  );

  return !hasContentRow;
};

export const getEmailListTooltipText = (emails: Array<string>): string => {
  return emails.join(', ');
};

// Common utility to swap placeholder and identifier within job match addon row
function swapJobMatchPlaceholderAndIdentifier(
  content: any,
  swapDirection: 'toIdentifier' | 'toPlaceholder',
): any {
  const emailRows = content?.page?.rows;
  if (emailRows) {
    emailRows.forEach(
      (row: {
        rowInternal: {uid: string};
        metadata: Record<string, any>;
        columns: Array<{
          modules: Array<{descriptor: {paragraph: {html: string}}}>;
        }>;
      }) => {
        if (row.rowInternal && row.rowInternal.uid === JOB_MATCH_ADDON_ID) {
          const variableIdentifier = row.metadata?.variableIdentifier;
          const placeholder = row.metadata?.placeholder;
          let moduleParagraph: string | undefined;
          try {
            moduleParagraph =
              row.columns[0].modules[0].descriptor.paragraph.html;
          } catch (error) {
            moduleParagraph = undefined;
          }
          if (swapDirection === 'toIdentifier') {
            // On normalize: from placeholder to variableIdentifier, also save placeholder
            if (variableIdentifier) {
              try {
                if (moduleParagraph) {
                  row.metadata.placeholder = moduleParagraph;
                }
                row.columns[0].modules[0].descriptor.paragraph.html =
                  variableIdentifier;
              } catch (error) {}
            }
          } else if (swapDirection === 'toPlaceholder') {
            // On parse: from variableIdentifier to placeholder, also save variableIdentifier
            if (placeholder) {
              try {
                if (moduleParagraph) {
                  row.metadata.placeholder = moduleParagraph;
                }
                row.columns[0].modules[0].descriptor.paragraph.html =
                  placeholder;
              } catch (error) {}
            }
          }
        }
      },
    );
  }
  return content;
}

export const normalizeJobMatchAddon = (
  contentJSON: string | null | undefined,
): string => {
  if (!contentJSON) {
    return '{}';
  }
  if (contentJSON === '{}') {
    return contentJSON;
  }
  const content = JSON.parse(contentJSON);
  const updatedContent = swapJobMatchPlaceholderAndIdentifier(
    content,
    'toIdentifier',
  );
  return JSON.stringify(updatedContent);
};

export const parseJobMatchAddon = (
  contentJSON: string | null | undefined,
): string => {
  if (!contentJSON) {
    return '{}';
  }
  if (contentJSON === '{}') {
    return contentJSON;
  }
  const content = JSON.parse(contentJSON);
  const updatedContent = swapJobMatchPlaceholderAndIdentifier(
    content,
    'toPlaceholder',
  );
  return JSON.stringify(updatedContent);
};
